import { DateTime } from 'luxon';
import {
  assoc,
  chain,
  invoker,
  partialRight,
  pipe,
  prop,
  prepend,
  head,
  length,
  ifElse,
  join,
  range,
  reduce,
  flip,
  gt,
  uniq,
} from 'ramda';
import {
  ACADEMIC_YEAR_START_DATE,
  ACADEMIC_YEAR_SWITCHOVER_DATE,
  CalendarDay,
  MONTH_START_FORMAT,
  SELECTED_DAY_FORMAT,
  TimetableDay,
  TimetableEvent,
  WEEK_START_FORMAT,
} from '@versita/shared/models';

const _gt = flip(gt);

const parseDateFormat = (format: string) => {
  const parseDate = partialRight(DateTime.fromFormat, [format]);
  const throwIfDateInvalid = (date: DateTime) => {
    if (!date.isValid) {
      throw new Error(`Invalid date format received. Expected: ${format}`);
    }
    return date;
  };
  return pipe(parseDate, throwIfDateInvalid);
};
const toDateFormat = (format: string) =>
  invoker(1, 'toFormat')(format) as (v: DateTime) => string;

// Parse formatted strings into Luxon DateTime objects
export const parseSelectedDay = parseDateFormat(SELECTED_DAY_FORMAT);
export const parseWeekStart = parseDateFormat(WEEK_START_FORMAT);
export const parseMonthStart = parseDateFormat(MONTH_START_FORMAT);

// Serialise Luxon DateTime into a specific string format
export const toSelectedDayFormat = toDateFormat(SELECTED_DAY_FORMAT);
export const toWeekStartFormat = toDateFormat(WEEK_START_FORMAT);
export const toMonthStartFormat = toDateFormat(MONTH_START_FORMAT);

/**
 * Returns the academic year for the current date.
 */
export function getCurrentAcademicYear(): `${number}.${number}` {
  const now = new Date();
  const { month, day } = ACADEMIC_YEAR_SWITCHOVER_DATE;
  const targetDate = new Date(now.getFullYear(), month - 1, day);
  return now < targetDate
    ? `${now.getFullYear() - 1}.${now.getFullYear()}`
    : `${now.getFullYear()}.${now.getFullYear() + 1}`;
}

/**
 * Returns the days to show for a given month in a calendar view
 * @param {string} calendarMonthStart - The starting month formatted as per
 *  {@link MONTH_START_FORMAT}.
 *
 * Note that this function will often return days that start before this
 * date in order to fill up the week completely.
 */
export function getCalendarDays(calendarMonthStart: string): CalendarDay[] {
  const monthStart = parseMonthStart(calendarMonthStart);
  const today = DateTime.now().startOf('day');
  const calendarStart = monthStart.startOf('week');
  const calendarEnd = monthStart.plus({ months: 1 }).endOf('week');
  const totalDays = Math.round(calendarEnd.diff(calendarStart, 'days').days);
  return range(0, totalDays).map((days) => {
    const date = calendarStart.plus({ days });
    return {
      weekStart: date.startOf('week').toFormat(WEEK_START_FORMAT),
      date: date.toFormat(SELECTED_DAY_FORMAT),
      current: date.equals(today),
      label: date.toFormat('d'),
      outsideCurrentMonth: !monthStart.hasSame(date, 'month'),
    };
  });
}

/**
 * Returns the days to show for a given week in a timetable view
 * @param {string} weekStart - The starting month formatted as per
 *  {@link WEEK_START_FORMAT}
 */
export function getTimetableWeekDays(weekStart: string): TimetableDay[] {
  const weekStartDate = parseWeekStart(weekStart);
  const today = DateTime.now().startOf('day');
  return range(0, 7).map((days) => {
    const date = weekStartDate.plus({ days });
    return {
      date: date.toISO() as string,
      current: date.equals(today),
      label: date.toFormat('EEE d'),
      weekDay: days + 1,
    };
  });
}

/**
 * Returns the day to show in a timetable view in the appropriate format
 * @param {string} day - The starting month formatted as per {@link SELECTED_DAY_FORMAT}
 */
export function getTimetableDay(day: string): TimetableDay {
  const date = parseSelectedDay(day);
  const today = DateTime.now().startOf('day');
  return {
    date: date.toISO() as string,
    current: date.equals(today),
    label: date.toFormat('EEE d'),
    weekDay: date.weekday,
  };
}

/** Returns the hours and minutes of a DateTime in decimal format (e.g. 16:30 becomes 16.5)
 * TODO: move to a date utils library instead of timetable utils
 */
export const getHourDecimal = (isoDate: string) => {
  const date = DateTime.fromISO(isoDate);
  return date.hour + date.minute / 60;
};

/** Factory to create a function that returns a color for a given identifier
 * The color returned will always be the same for a given identifier and will not be
 * re-used for other identifiers unless all colors have been used.
 * TODO: move to a color utils library or something similar
 */
export const createColorAssigner = () => {
  const colors = [
    '#fabed4',
    '#3cb44b',
    '#4c6ce1',
    '#ffe119',
    '#f58231',
    '#42d4f4',
    '#f032e6',
    '#469990',
    '#dcbeff',
    '#a0692a',
    '#fffac8',
    '#e6194B',
    '#aaffc3',
    '#6767dc',
    '#c94949',
  ];
  function* colorGenerator(): Generator<string> {
    let i = 0;
    while (true) {
      yield colors[i];
      // Cheeky way to infinitely loop through the colors.
      i = (i + 1) % colors.length;
    }
  }
  const colorIterator = colorGenerator();
  const assignedColors = new Map();

  return (identifier: string) => {
    if (!assignedColors.has(identifier)) {
      assignedColors.set(identifier, colorIterator.next().value);
    }
    return assignedColors.get(identifier);
  };
};

/** singleton instance of the color assigner dedicated to
 * assigning category colors (see: {@link createColorAssigner}) */
export const assignCategoryColor = createColorAssigner();

export interface TimetableCategory {
  name: string;
  visible: boolean;
}

export type TimetableCategories = Record<
  string /* context (timetable ID) */,
  Record<string /* EventTypeID */, TimetableCategory>
>;

export const extractTimetableItemsCategories = pipe(
  chain<TimetableEvent, string>(prop('moduleNames')),
  uniq,
  reduce<
    /* input (array of module names): */ string,
    /* output: */ TimetableCategories[string]
  >(
    (acc, moduleName) =>
      assoc(
        moduleName,
        {
          name: moduleName,
          visible: true,
        },
        acc,
      ),
    {},
  ),
);

const moreThanOneModule = pipe(length, _gt(1));
const formatMultiModuleLabel = pipe(prepend('Multiple modules:'), join('\n- '));

export const getTimetableEventName = ifElse(
  moreThanOneModule,
  formatMultiModuleLabel,
  head,
) as (moduleNames: string[]) => string;

export const getWeekStartDateNumber = (dateString: string) => {
  const weekStart = parseWeekStart(dateString);
  const academicYearStart = parseWeekStart(ACADEMIC_YEAR_START_DATE);
  if (weekStart < academicYearStart) {
    return -1;
  }
  const weekNumber = weekStart.diff(academicYearStart, 'weeks').weeks;
  return weekNumber > 51 ? -1 : weekNumber;
};

export const getWeekStartNumberDate = (weekNumber: number) =>
  toWeekStartFormat(
    parseWeekStart(ACADEMIC_YEAR_START_DATE).plus({ weeks: weekNumber }),
  );

/** Generic function signature representing a function that either increments or
 * decrements a given date by 1 day/week/month */
export type PeriodAdjustor = (op: 'plus' | 'minus') => (v: string) => string;

/** adds or subtracts a week from a given week formatted as per WEEK_START_FORMAT */
export const adjustDayByOne: PeriodAdjustor = (op) =>
  pipe(parseSelectedDay, invoker(1, op)({ days: 1 }), toSelectedDayFormat);

/** adds or subtracts a week from a given week formatted as per WEEK_START_FORMAT */
export const adjustWeekByOne: PeriodAdjustor = (op) =>
  pipe(parseWeekStart, invoker(1, op)({ weeks: 1 }), toWeekStartFormat);

/** adds or subtracts a month from a given month formatted as per MONTH_START_FORMAT */
export const adjustMonthByOne: PeriodAdjustor = (op) =>
  pipe(parseMonthStart, invoker(1, op)({ months: 1 }), toMonthStartFormat);

export const startOf = invoker(1, 'startOf');
